<template>
  <base-card head="user_details" :hasBack="true" class="lg:max-w-5xl" size="lg">
    <AdminUserCreate v-if="$store.getters['auth/isStaff']" />
    <template v-else>
      <FormError :errors="errors" />
      <div class="flex space-x-8">
        <dl class="w-full">
          <div class="bg-white px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
            <dt class="text-lg leading-5 font-medium text-secondary-dark">
              {{ $t(`mygwf.components.Form.email`) }} :
              <span class="text-red-default">*</span>
            </dt>
            <dd class="mt-1 leading-5 sm:mt-0 sm:col-span-2">
              <base-input v-model="user.email" type="email" />
            </dd>
          </div>
          <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
            <dt class="text-lg leading-5 font-medium text-secondary-dark">
              {{ $t(`mygwf.components.Form.role`) }}
            </dt>
            <dd
              class="mt-1 leading-5 sm:mt-0 sm:col-span-2 space-x-4 flex items-center justify-between"
            >
              <div class="flex flex-col justify-center my-6 space-y-5">
                <label>
                  <input :value="true" v-model="isCompanyAdmin" type="radio" />
                  <span class="ml-2">Company Admin</span>
                </label>
                <label>
                  <input :value="false" v-model="isCompanyAdmin" type="radio" />
                  <span class="ml-2">Basic User</span>
                </label>
              </div>
            </dd>
          </div>
          <div
            v-if="$store.getters['auth/activeProfile'].company_is_distributor && isCompanyAdmin"
            class="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center"
          >
            <dt class="text-lg leading-5 font-medium text-secondary-dark">
              {{ $t(`mygwf.components.Form.company`) }}
            </dt>
            <dd class="mt-1 leading-5">
              <SingleSelect
                :options="$store.getters['companies/list']"
                class="w-full shadow p-4 text-sm"
                v-model="user.company"
              />
            </dd>
          </div>
          <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
            <dt class="text-lg leading-5 font-medium text-secondary-dark">
              {{ $t(`mygwf.components.Form.permissions`) }}
            </dt>
            <dd class="mt-1 leading-5 sm:mt-0 flex items-center justify-between">
              <BaseTree
                v-model="permissions"
                :options="permissionsOptions"
                :disabled="isCompanyAdmin"
              />
            </dd>
          </div>
        </dl>
      </div>
      <div class="flex space-x-3 py-5 px-4 my-1 justify-end border-t">
        <span class="self-center mr-auto"
          ><span class="text-red-default">*</span> {{ requiredFieldText }}</span
        >
        <base-button @click="$router.back()" action="close" context="secondary">
          <base-icon icon="close" />
        </base-button>
        <base-button
          @click="submit()"
          action="save"
          :loading="loading"
          :disabled="$v.user.$invalid"
        >
          <base-icon icon="success" />
        </base-button>
      </div>
    </template>
  </base-card>
</template>

<script>
import {BaseButton, BaseCard, BaseIcon, BaseInput, BaseSelect, BaseTree} from '@/components/base'
import isEmpty from 'lodash-es/isEmpty'
import SingleSelect from '@/components/shared/SingleSelect.vue'
import {required, email} from 'vuelidate/lib/validators'

export default {
  data() {
    return {
      user: {},
      errors: {},
      isCompanyAdmin: true,
      loading: false,
      permissions: [],
      // These permissions don't actually exist
      permissionGroups: ['0', '1', '2', '3'],
    }
  },
  watch: {
    isCompanyAdmin: {
      immediate: true,
      handler(n, o) {
        if (n) {
          this.permissions = [...this.permissionGroups]
        }
      },
    },
  },
  methods: {
    async submit() {
      this.loading = true
      const user = {...this.user}
      if (this.isCompanyAdmin) {
        user.is_admin = true
      } else {
        user.permissions = this._flattenPermissions()
      }
      if (
        !(this.$store.getters['auth/activeProfile'].company_is_distributor && this.isCompanyAdmin)
      ) {
        user.company = this.$store.getters['auth/activeCompany']
      }

      try {
        await this.$store.dispatch(`users/create`, user)
        this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: `created`,
        })
        this.user = {}
        this.errors = {}
      } catch (errors) {
        this.errors = errors
      } finally {
        this.loading = false
      }
    },

    _flattenPermissions() {
      const userPermissions = this.permissions.filter(p => +p > 3)
      if (!isEmpty(userPermissions)) {
        return userPermissions
      }
      return this.permissionsOptions.flatMap(x =>
        x.children.reduce((acc2, curr) => [...acc2, curr.key], [])
      )
    },
  },
  computed: {
    fields() {
      return this.$store.getters['users/createFields']
    },
    permissionsOptions() {
      const userPerms = [],
        companyPerms = [],
        devicesPerms = [],
        reportsPerms = []
      this.$store.getters['companies/permissionsById'](this.user.company).forEach(p => {
        const permGroup = p.label.split('.')[0]
        switch (permGroup) {
          case 'authsystem':
            userPerms.push({
              key: p.id,
              title: this.$t(`mygwf.permissions.${p.label}`),
            })
            break
          case 'companies':
            companyPerms.push({
              key: p.id,
              title: this.$t(`mygwf.permissions.${p.label}`),
            })
            break
          case 'devices':
            devicesPerms.push({
              key: p.id,
              title: this.$t(`mygwf.permissions.${p.label}`),
            })
            break
          case 'reports':
            reportsPerms.push({
              key: p.id,
              title: this.$t(`mygwf.permissions.${p.label}`),
            })
        }
      })
      const permOptions = [
        {
          key: '0',
          title: this.$t(`mygwf.permissions.user_management`),
          children: userPerms,
        },
        {
          key: '1',
          title: this.$t(`mygwf.permissions.company_management`),
          children: companyPerms,
        },
        {
          key: '2',
          title: this.$t(`mygwf.permissions.device_management`),
          children: devicesPerms,
        },
        {
          key: '3',
          title: this.$t(`mygwf.permissions.report_management`),
          children: reportsPerms,
        },
      ]
      return permOptions
    },
    requiredFieldText() {
      return this.$t('mygwf.components.Form.requiredField')
    },
  },
  validations: {
    user: {
      email: {
        required,
        email,
      },
    },
  },
  components: {
    BaseButton,
    BaseCard,
    BaseIcon,
    BaseInput,
    BaseSelect,
    SingleSelect,
    BaseTree,
    FormError: () => import('@/components/shared/FormError.vue'),
    AdminUserCreate: () => import('./AdminUserCreate.vue'),
  },
}
</script>
